import { useState, useEffect, useMemo, useRef } from "react";
import { safeDistanceToNow } from "@/lib/safeDate";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { clearAppBadge } from "@/lib/nativePushNotifications";
import { getNotificationIcon, resolveNotificationLink } from "@/lib/notificationTypes";

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  read: boolean;
  data: unknown;
  created_at: string;
  entity_type?: string | null;
  entity_id?: string | null;
  link?: string | null;
}

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<"all" | "unread">("all");

  // A single in-flight fetch owns the state: a realtime burst, the poll and a
  // re-mount cannot interleave and produce a stale or duplicated list.
  const fetchSeq = useRef(0);

  const fetchNotifications = async () => {
    const seq = ++fetchSeq.current;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);

    if (seq !== fetchSeq.current) return; // a newer fetch has taken over
    if (error) {
      console.error("Error fetching notifications:", error);
      return;
    }

    // Defensive de-duplication: realtime reconnects can re-deliver a change,
    // and the row id is the only identity that matters here.
    const rows = (data ?? []) as Notification[];
    const unique = Array.from(new Map(rows.map((n) => [n.id, n])).values());
    setNotifications(unique);
    setUnreadCount(unique.filter((n) => !n.read).length);
  };

  useEffect(() => {
    fetchNotifications();

    // Auto-refresh every 30 seconds (kept intentionally; may be redundant after realtime filter — flag for manual review)
    const interval = setInterval(fetchNotifications, 30_000);

    // Set up real-time subscription scoped to current user
    const setupChannel = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      return supabase
        .channel(`notifications-realtime-${user.id}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "notifications",
            filter: `user_id=eq.${user.id}`,
          },
          () => {
            fetchNotifications();
          }
        )
        .subscribe();
    };

    let channelRef: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;
    setupChannel().then(ch => {
      channelRef = ch;
      // Unmounted before the channel resolved: tear it down immediately so a
      // stale subscription can never outlive the component.
      if (cancelled && ch) supabase.removeChannel(ch);
    });

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (channelRef) supabase.removeChannel(channelRef);
    };
  }, []);

  const markAsRead = async (notificationId: string) => {
    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("id", notificationId);

    if (!error) {
      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const markAllAsRead = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("user_id", user.id)
      .eq("read", false);

    if (!error) {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      clearAppBadge();
    }
  };

  const clearAllNotifications = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("user_id", user.id);

    if (!error) {
      setNotifications([]);
      setUnreadCount(0);
      clearAppBadge();
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      void markAsRead(notification.id);
    }
    const target = resolveNotificationLink(notification);
    if (target) {
      setOpen(false);
      navigate(target);
    }
  };

  const visible = useMemo(
    () => (filter === "unread" ? notifications.filter((n) => !n.read) : notifications),
    [notifications, filter],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-destructive text-destructive-foreground"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Notifications</h3>
          <div className="flex gap-1">
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs h-7 px-2"
                onClick={markAllAsRead}
              >
                Mark read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs h-7 px-2 text-destructive hover:text-destructive"
                onClick={clearAllNotifications}
              >
                Clear all
              </Button>
            )}
          </div>
        </div>
        {notifications.length > 0 && (
          <div className="flex gap-1 border-b px-4 py-2">
            <Button
              variant={filter === "all" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 px-3 text-xs"
              onClick={() => setFilter("all")}
            >
              All
            </Button>
            <Button
              variant={filter === "unread" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 px-3 text-xs"
              onClick={() => setFilter("unread")}
            >
              Unread{unreadCount > 0 ? ` (${unreadCount})` : ""}
            </Button>
          </div>
        )}
        <ScrollArea className="h-80">
          {visible.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                {notifications.length === 0 ? "No notifications yet" : "Nothing unread"}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {visible.map((notification) => (
                <div
                  key={notification.id}
                  role="button"
                  tabIndex={0}
                  className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${
                    !notification.read ? "bg-primary/5" : ""
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleNotificationClick(notification);
                    }
                  }}
                >
                  <div className="flex gap-3">
                    <span className="text-lg">
                      {getNotificationIcon(notification.type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium ${!notification.read ? "text-foreground" : "text-muted-foreground"}`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {safeDistanceToNow(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
